import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  getTeamLeaveCalendar,
  syncEmployeeLeaves,
} from "../../../core/data/redux/leaveSlice";
import LeaveModuleTabs from "./LeaveModuleTabs";

// Status Types and Color Configuration matching Mobile App (dmrc-hrms-mobileapp-dev_qa)
export interface EventStatusConfig {
  label: string;
  bg: string;
  color: string;
}

export const EVENT_STATUS_TYPES: Record<string, EventStatusConfig> = {
  sent: { label: "Sent", bg: "#FFE4B5", color: "#855d14" },
  absent: { label: "Absent", bg: "#87CEEB", color: "#0d5c80" },
  "non-working": { label: "Non-Working Day", bg: "#D3D3D3", color: "#495057" },
  travel: { label: "Travel", bg: "#90EE90", color: "#1e6b1e" },
  holiday: { label: "Holiday", bg: "#FFFF99", color: "#857200" },
  multiple: { label: "Multiple Entries", bg: "#DDA0DD", color: "#5b1d5b" },
  deletion: { label: "Deletion Requested", bg: "#FFB6C1", color: "#8b1a2b" },
};

function mapEventDescriptionToType(desc: string, eventType?: string): string {
  const d = (desc || "").toLowerCase();
  const evt = (eventType || "").trim();

  if (evt === "00" || d.includes("non-working") || d.includes("non working")) {
    return "non-working";
  }
  if (
    evt === "06" ||
    d.includes("-nh") ||
    d.includes("-rh") ||
    d.includes("-gh") ||
    d.includes("holiday")
  ) {
    return "holiday";
  }
  if (
    evt === "01" ||
    d.includes("leave") ||
    d.includes("absent") ||
    d.includes("quarantine") ||
    d.includes("earned") ||
    d.includes("approved")
  ) {
    return "absent";
  }
  if (d.includes("sent") || d.includes("pending")) return "sent";
  if (d.includes("travel")) return "travel";
  if (d.includes("multiple")) return "multiple";
  if (d.includes("deletion")) return "deletion";
  return "holiday";
}

export function formatCalendarEventDescription(desc: string): string {
  if (!desc || typeof desc !== "string") return "";
  let clean = desc.trim();

  // Strip leading date range: e.g. "16.10.26 - 19.10.26 ", "16.10.2026 - 19.10.2026 ", "16/10/26 - 19/10/26 "
  clean = clean.replace(
    /^\s*\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\s*(?:-\s*\d{1,2}[./-]\d{1,2}[./-]\d{2,4})?\s*[:\-–]?\s*/i,
    ""
  );

  // Strip trailing status: e.g. " Approved", " Sent", " Pending", " Rejected", " Requested", " Cancelled"
  clean = clean.replace(
    /\s+(approved|sent|pending|rejected|requested|cancelled|draft)\s*$/i,
    ""
  );

  // Normalize hyphenated Regular leave types (e.g. "Earned Leave-Regular" -> "Earned Leave Regular")
  clean = clean.replace(/\b([a-zA-Z]+)-Regular\b/gi, "$1 Regular");

  return clean.trim();
}

function parseODataToDateObj(dateStr: string | null | undefined): Date | null {
  if (!dateStr || typeof dateStr !== "string") return null;
  const match = dateStr.match(/\/Date\((\d+)\)\//);
  if (match) {
    return new Date(parseInt(match[1], 10));
  }
  const d = new Date(dateStr);
  return !isNaN(d.getTime()) ? d : null;
}

function parseODataToYMD(dateStr: string | null | undefined): string {
  if (!dateStr || typeof dateStr !== "string") return "";
  const match = dateStr.match(/\/Date\((\d+)\)\//);
  if (match) {
    const date = new Date(parseInt(match[1], 10));
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
}

const LeaveManagementTeamCalendar: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const routes = all_routes;
  const { teamLeaveCalendar, loading } = useSelector(
    (state: any) => state.leavesState || state.leave || {}
  );

  // Read empId from localStorage
  const userJson = useMemo(() => {
    const raw = localStorage.getItem("user");
    return raw && raw !== "undefined" ? JSON.parse(raw) : {};
  }, []);
  const empId: string = (location.state as any)?.overrideEmployeeID || userJson?.empId || "";

  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [syncing, setSyncing] = useState<boolean>(false);
  const [selectedCellInfo, setSelectedCellInfo] = useState<{
    memberName: string;
    dateStr: string;
    status: string;
  } | null>(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth() + 1; // 1-indexed (1..12)

  // Fetch Team Leave Calendar for selected month (from: YYYY-MM-01, to: YYYY-MM-lastDay)
  const fetchCalendarData = () => {
    if (!empId) return;
    const daysInMonth = new Date(year, month, 0).getDate();
    const formattedEmpId = String(empId).trim().padStart(8, "0");
    const from = `${year}-${String(month).padStart(2, "0")}-01`;
    const to = `${year}-${String(month).padStart(2, "0")}-${String(
      daysInMonth
    ).padStart(2, "0")}`;

    dispatch(
      getTeamLeaveCalendar({
        empId: formattedEmpId,
        from,
        to,
      }) as any
    );
  };

  useEffect(() => {
    fetchCalendarData();
  }, [dispatch, empId, year, month]);

  // Sync leave data with SAP API (matches app sync functionality)
  const handleSyncData = async () => {
    if (!empId) return;
    setSyncing(true);
    toast.info("Syncing leave data with SAP...");
    try {
      const formattedEmpId = String(empId).trim().padStart(8, "0");
      const daysInMonth = new Date(year, month, 0).getDate();
      const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const toDate = `${year}-${String(month).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;
      await dispatch(
        syncEmployeeLeaves({
          empId: formattedEmpId,
          fromDate,
          toDate,
        }) as any
      ).unwrap();
      toast.success("Leave data synced successfully!");
      fetchCalendarData();
    } catch (err: any) {
      console.error("Sync error:", err);
      toast.error(err || "Failed to sync leave data.");
    } finally {
      setSyncing(false);
    }
  };

  // Parse response data into members list and leave mapping per member & date
  const { teamMembers, leaveDataMap } = useMemo(() => {
    const rawList: any[] = Array.isArray(teamLeaveCalendar?.data?.data)
      ? teamLeaveCalendar.data.data
      : Array.isArray(teamLeaveCalendar?.data)
      ? teamLeaveCalendar.data
      : Array.isArray(teamLeaveCalendar)
      ? teamLeaveCalendar
      : [];

    const memberMap = new Map<
      string,
      { id: string; name: string; isRequester: boolean; rowNumber: number }
    >();
    const leaveData: Record<
      string,
      Record<string, { type: string; status: string }>
    > = {};

    rawList.forEach((item: any) => {
      const id = String(item.EmployeeID || "").trim();
      const name = String(item.Name || `Employee (${id})`).trim();
      if (id) {
        if (!memberMap.has(id)) {
          memberMap.set(id, {
            id,
            name,
            isRequester: !!item.IsRequester,
            rowNumber: item.RowNumber ?? 999,
          });
        }
      }

      const desc = item.CalendarEventDescription || "";
      const eventType = item.CalendarEventType;
      const mappedType = mapEventDescriptionToType(desc, eventType);

      if (!leaveData[id]) leaveData[id] = {};

      const startDate = parseODataToDateObj(item.StartDate);
      const endDate = parseODataToDateObj(item.EndDate) || startDate;

      if (startDate && endDate && startDate <= endDate) {
        const curr = new Date(startDate);
        let count = 0;
        while (curr <= endDate && count < 366) {
          const y = curr.getFullYear();
          const m = String(curr.getMonth() + 1).padStart(2, "0");
          const d = String(curr.getDate()).padStart(2, "0");
          const curYMD = `${y}-${m}-${d}`;

          const existing = leaveData[id][curYMD];
          if (!existing) {
            leaveData[id][curYMD] = {
              type: mappedType,
              status: desc,
            };
          } else {
            // Holiday (06) takes precedence over generic non-working day or standard leave
            if (eventType === "06" || mappedType === "holiday") {
              leaveData[id][curYMD] = {
                type: mappedType,
                status: desc,
              };
            } else if (
              existing.type === "non-working" &&
              (eventType === "01" || mappedType === "absent")
            ) {
              // Approved Leave takes precedence over generic weekend Non-Working Day
              leaveData[id][curYMD] = {
                type: mappedType,
                status: desc,
              };
            }
          }

          curr.setDate(curr.getDate() + 1);
          count++;
        }
      } else {
        const singleYMD = parseODataToYMD(item.StartDate);
        if (singleYMD) {
          leaveData[id][singleYMD] = {
            type: mappedType,
            status: desc,
          };
        }
      }
    });

    const members = Array.from(memberMap.values()).sort(
      (a, b) => a.rowNumber - b.rowNumber
    );

    return { teamMembers: members, leaveDataMap: leaveData };
  }, [teamLeaveCalendar]);

  // Month navigation handlers
  const handlePrevMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  // Calendar grid calculation for vertical (Sun-Sat 7-column) view
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayIndex = new Date(year, month - 1, 1).getDay(); // 0 = Sun ... 6 = Sat

  const calendarGridCells: (number | null)[] = useMemo(() => {
    const cells: (number | null)[] = Array(firstDayIndex).fill(null);
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push(i);
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return cells;
  }, [daysInMonth, firstDayIndex]);

  const monthName = currentMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() + 1 === month;

  return (
    <div className="page-wrapper">
      <div className="content">
        <LeaveModuleTabs />
        {/* Page Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 gap-2">
          <div className="d-flex align-items-center">
            <Link
              to={routes.LeaveManagement.list}
              state={location.state}
              className="text-secondary me-3 text-decoration-none bg-white border d-flex align-items-center justify-content-center shadow-sm rounded-circle"
              style={{ width: "40px", height: "40px" }}
              title="Back to Leave List"
            >
              <i className="ti ti-arrow-left fs-18"></i>
            </Link>
            <div>
              <h4 className="mb-0 fw-bold text-danger">Team Calendar</h4>
              <span className="text-muted fs-13">
                View monthly leave schedules for your entire team
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-danger btn-sm shadow-sm d-flex align-items-center"
              onClick={handleSyncData}
              disabled={syncing}
              title="Sync Leave Data with SAP"
            >
              <i
                className={`ti ti-refresh me-1 ${
                  syncing ? "spin-animation" : ""
                }`}
              />
              {syncing ? "Syncing..." : "Sync Data"}
            </button>

            <button
              className="btn btn-outline-secondary btn-sm shadow-sm"
              onClick={handleToday}
            >
              <i className="ti ti-calendar-event me-1" />
              Today
            </button>

            {loading === "pending" && (
              <span className="badge bg-light text-secondary border fs-13 px-3 py-2">
                <span
                  className="spinner-border spinner-border-sm me-2 text-danger"
                  role="status"
                />
                Loading...
              </span>
            )}
          </div>
        </div>

        {/* Legend Card */}
        <div className="card border-0 shadow-sm rounded-3 mb-3">
          <div className="card-body py-2 px-3">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="fw-semibold text-dark fs-13 me-1">Legend:</span>
              {Object.entries(EVENT_STATUS_TYPES).map(([typeKey, cfg]) => (
                <div
                  key={typeKey}
                  className="d-flex align-items-center me-2 py-1"
                >
                  <span
                    className="d-inline-block border rounded me-1"
                    style={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: cfg.bg,
                    }}
                  />
                  <span className="fs-12 text-secondary">{cfg.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Team Calendar Card */}
        <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
          {/* Header Controls (Month Navigator) */}
          <div className="card-header bg-white p-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center">
              <button
                className="btn btn-sm btn-light border shadow-sm me-2"
                onClick={handlePrevMonth}
                title="Previous Month"
              >
                <i className="ti ti-chevron-left text-danger fs-16"></i>
              </button>
              <h4
                className="mb-0 mx-2 fw-bold text-dark"
                style={{ minWidth: "200px", textAlign: "center" }}
              >
                {monthName}
              </h4>
              <button
                className="btn btn-sm btn-light border shadow-sm ms-2"
                onClick={handleNextMonth}
                title="Next Month"
              >
                <i className="ti ti-chevron-right text-danger fs-16"></i>
              </button>
            </div>

            <div className="text-muted fs-13">
              Showing <strong className="text-dark">{teamMembers.length}</strong> team members
            </div>
          </div>

          {/* Calendar Grid Table (Vertical 7-column layout per member) */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table
                className="table table-bordered mb-0 align-middle"
                style={{ width: "100%", minWidth: "900px", tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "calc(100% - 220px)" }} />
                </colgroup>
                <tbody>
                  {teamMembers.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center py-5 text-muted">
                        {loading === "pending" ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2 text-danger" />
                            Loading team calendar data...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-calendar-off fs-24 d-block mb-2 text-secondary" />
                            No team calendar data found for {monthName}
                          </>
                        )}
                      </td>
                    </tr>
                  ) : (
                    teamMembers.map((member) => {
                      const memberLeaves = leaveDataMap[member.id] || {};
                      return (
                        <tr key={member.id}>
                          {/* Employee Details Column */}
                          <td
                            className="align-middle"
                            style={{
                              width: "220px",
                              borderRight: "2px solid #dee2e6",
                              backgroundColor: member.isRequester
                                ? "#fff3f3"
                                : "#f8f9fa",
                            }}
                          >
                            <div className="p-2">
                              {member.isRequester && (
                                <span
                                  className="badge bg-danger mb-1 fs-10"
                                  style={{ fontSize: "10px" }}
                                >
                                  You
                                </span>
                              )}
                              <div className="fw-bold fs-13 text-dark text-truncate">
                                {member.name}
                              </div>
                              <div className="text-muted fs-11">
                                ID: {member.id}
                              </div>
                            </div>
                          </td>

                          {/* 7-column Vertical Grid for Days */}
                          <td className="p-0">
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                                width: "100%",
                                gap: "1px",
                                backgroundColor: "#dee2e6",
                              }}
                            >
                              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                                (dayName) => (
                                  <div
                                    key={dayName}
                                    className="text-center bg-white py-1 fs-11 fw-bold text-muted text-truncate"
                                    style={{ minWidth: 0, width: "100%" }}
                                  >
                                    {dayName}
                                  </div>
                                )
                              )}
                              {calendarGridCells.map((dayNum, cIdx) => {
                                const dateYMD = dayNum
                                  ? `${year}-${String(month).padStart(
                                      2,
                                      "0"
                                    )}-${String(dayNum).padStart(2, "0")}`
                                  : null;
                                const leaveItem = dateYMD
                                  ? memberLeaves[dateYMD]
                                  : null;
                                const cfg = leaveItem
                                  ? EVENT_STATUS_TYPES[leaveItem.type] || {
                                      label: leaveItem.status,
                                      bg: "#FFE4B5",
                                      color: "#855d14",
                                    }
                                  : null;

                                const isToday =
                                  isCurrentMonth && dayNum === today.getDate();

                                const displayTitle = leaveItem?.status
                                  ? formatCalendarEventDescription(leaveItem.status)
                                  : cfg?.label || "";

                                return (
                                  <div
                                    key={cIdx}
                                    className="text-center d-flex flex-column justify-content-start align-items-center p-1 position-relative"
                                    style={{
                                      minHeight: "74px",
                                      minWidth: 0,
                                      width: "100%",
                                      boxSizing: "border-box",
                                      backgroundColor: dayNum
                                        ? cfg
                                          ? cfg.bg
                                          : "#ffffff"
                                        : "#fafafa",
                                      border: isToday
                                        ? "2px solid #e12b2b"
                                        : undefined,
                                      cursor: cfg ? "pointer" : "default",
                                    }}
                                    title={
                                      cfg && dateYMD
                                        ? `${member.name} (${dateYMD}): ${leaveItem?.status || cfg?.label}`
                                        : dateYMD || undefined
                                    }
                                    onClick={() => {
                                      if (cfg && leaveItem && dateYMD) {
                                        setSelectedCellInfo({
                                          memberName: member.name,
                                          dateStr: dateYMD,
                                          status: leaveItem.status || cfg.label,
                                        });
                                      }
                                    }}
                                  >
                                    {dayNum && (
                                      <>
                                        <div
                                          className="fs-12 fw-bold"
                                          style={{
                                            color: cfg ? cfg.color : "#333",
                                            lineHeight: "1.1",
                                          }}
                                        >
                                          {dayNum}
                                        </div>
                                        {displayTitle ? (
                                          <div
                                            className="px-1 mt-1 text-center w-100"
                                            style={{
                                              color: cfg ? cfg.color : "#333",
                                              fontWeight: 600,
                                              fontSize: "9px",
                                              lineHeight: "1.2",
                                              wordBreak: "break-word",
                                              overflowWrap: "break-word",
                                              whiteSpace: "normal",
                                            }}
                                            title={leaveItem?.status || displayTitle}
                                          >
                                            {displayTitle}
                                          </div>
                                        ) : null}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Event Details Modal */}
        {selectedCellInfo && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content shadow border-0">
                <div className="modal-header py-2 bg-light">
                  <h6 className="modal-title fw-bold text-danger">
                    <i className="ti ti-info-circle me-1" /> Event Details
                  </h6>
                  <button
                    type="button"
                    className="btn-close fs-12"
                    onClick={() => setSelectedCellInfo(null)}
                  />
                </div>
                <div className="modal-body py-3">
                  <div className="mb-2">
                    <strong className="text-dark fs-13">Employee:</strong>
                    <div className="fs-13 text-secondary">
                      {selectedCellInfo.memberName}
                    </div>
                  </div>
                  <div className="mb-2">
                    <strong className="text-dark fs-13">Date:</strong>
                    <div className="fs-13 text-secondary">
                      {selectedCellInfo.dateStr}
                    </div>
                  </div>
                  <div>
                    <strong className="text-dark fs-13">Event:</strong>
                    <div className="fs-13 fw-semibold text-danger mt-1">
                      {formatCalendarEventDescription(selectedCellInfo.status)}
                    </div>
                  </div>
                  {selectedCellInfo.status &&
                    selectedCellInfo.status !==
                      formatCalendarEventDescription(selectedCellInfo.status) && (
                      <div className="mt-2 pt-2 border-top">
                        <strong className="text-muted fs-11">Full SAP Description:</strong>
                        <div className="fs-11 text-secondary">
                          {selectedCellInfo.status}
                        </div>
                      </div>
                    )}
                </div>
                <div className="modal-footer py-1 border-0">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setSelectedCellInfo(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveManagementTeamCalendar;
